import type {
  AdvancedNumericFieldConfig,
  SelectOption,
  SystemCheckboxConfig,
} from '../types/pbxSettings.types'

export const QUERY_LIMIT = 500
export const PHONE_BATCH_SIZE = 50
export const PHONE_BATCH_MAX = 500
export const PHONE_BATCH_TIMEOUT = 90_000
export const STALE_TIME_SHORT = 5 * 60 * 1000
export const STALE_TIME_LONG = 30 * 60 * 1000
export const GC_TIME_LONG = 60 * 60 * 1000
export const DAY_NIGHT_SLOT_COUNT = 10

export const KNOWN_CODEC_OPTIONS = ['G729', 'G722', 'OPUS', 'PCMU']

export const DISABLED_CODEC_OPTIONS = new Set(['G729'])

export const ADVANCED_NUMERIC_FIELDS: AdvancedNumericFieldConfig[] = [
  {
    key: 'maximum_allowed_channels',
    label: 'Max Channels',
    min: 2,
    max: 500,
  },
  { key: 'maximum_lines_in', label: 'Inbound Channels', min: 2, max: 500 },
  {
    key: 'maximum_lines_out',
    label: 'Per Extension Channels',
    min: 1,
    max: 10,
  },
  {
    key: 'call_log_retention_period_in_months',
    label: 'Call History (in months)',
    min: 12,
    max: 120,
  },
]

export const SYSTEM_CHECKBOX_FIELDS: SystemCheckboxConfig[] = [
  {
    label: 'Inbound Call Recording',
    required: true,
    target: 'systemSettings',
    key: 'enable_inbound_recording',
  },
  {
    label: 'Outbound Call Recording',
    required: true,
    target: 'systemSettings',
    key: 'enable_outbound_recording',
  },
  {
    label: 'Internal Call Recording',
    required: true,
    target: 'systemSettings',
    key: 'enable_internal_recording',
  },
  { label: 'International', target: 'pbx', key: 'international' },
  {
    label: 'Voicemail Transcription',
    target: 'systemSettings',
    key: 'enable_vm_transcription',
  },
  {
    label: 'Schedule V2',
    target: 'systemSettings',
    key: 'schedules_v2',
    internalOnly: true,
    readOnlyWhenTrue: true,
  },
  {
    label: 'Call Center',
    target: 'systemSettings',
    key: 'enable_agent_statuses',
    internalOnly: true,
  },
  {
    label: 'Mango AI',
    target: 'systemSettings',
    key: 'enable_call_analytics',
  },
  { label: 'Grove', target: 'pbx', key: 'is_grove', readOnly: true },
]

export const INDUSTRY_OPTIONS: SelectOption[] = [
  'Automotive',
  'Call Center',
  'Chiropractic',
  'Construction',
  'Consulting',
  'CPA',
  'Demo',
  'Dental',
  'Education',
  'Enterprise',
  'Fitness',
  'Hospitality',
  'Insurance',
  'Legal',
  'Medical',
  'Non-Profit',
  'Pest Control',
  'Optometry',
  'Orthodontics',
  'Real Estate',
  'Service',
  'Technology',
  'Veterinarian',
  'Other',
].map((value) => ({ value, label: value }))

// EHR options mirror Pbx.EhrType in mango-api: `value` is the stored,
// reporting key and `label` is the customer-facing display name. Keep this
// list in lockstep with the backend enum.
export const EHR_OPTIONS: SelectOption[] = [
  { value: 'Other', label: 'Other' },
  { value: 'abeldent', label: 'ABELDent' },
  { value: 'Archy', label: 'Archy' },
  { value: 'asprodental', label: 'Asprodental' },
  { value: 'athena_health', label: 'Athena Health' },
  { value: 'avimark', label: 'AviMark' },
  { value: 'blueprint', label: 'Blueprint' },
  { value: 'CareStack', label: 'CareStack' },
  { value: 'cs_practice_wrks', label: 'Care Stream Practice Works' },
  { value: 'charm', label: 'Charm' },
  { value: 'Chiro Cat', label: 'Chiro Cat' },
  { value: 'chirotouch', label: 'ChiroTouch' },
  { value: 'cleardent', label: 'ClearDent' },
  { value: 'Cloud 9', label: 'Cloud 9' },
  { value: 'cornerstone', label: 'Cornerstone' },
  { value: 'cc_stack_mgmt', label: 'Crystal Care Stack MGMT' },
  { value: 'crystalpm', label: 'Crystal Practice Management' },
  { value: 'Curve', label: 'Curve' },
  { value: 'daysmart', label: 'DaySmart' },
  { value: 'ds_solutions', label: 'Dental Sleep Solutions' },
  { value: 'dentally', label: 'Dentally' },
  { value: 'denticon', label: 'Denticon' },
  { value: 'Dentrix', label: 'Dentrix' },
  { value: 'Dentrix Ascend', label: 'Dentrix Ascend' },
  { value: 'Dolphin', label: 'Dolphin' },
  { value: 'Eaglesoft', label: 'Eaglesoft' },
  { value: 'endovision', label: 'EndoVision' },
  { value: 'eye_cloud_pro', label: 'EyeCloud Pro' },
  { value: 'eyefinity', label: 'Eyefinity' },
  { value: 'ezderm', label: 'EZDerm' },
  { value: 'ezyvet', label: 'EzyVet' },
  { value: 'fuse', label: 'Fuse' },
  { value: 'Greyfinch', label: 'Greyfinch' },
  { value: 'identalsoft', label: 'iDentalSoft' },
  { value: 'impromed', label: 'Impromed' },
  { value: 'instinct_science', label: 'Instinct Science' },
  { value: 'jane', label: 'Jane' },
  { value: 'legwork', label: 'Legwork' },
  { value: 'lighthouse', label: 'Lighthouse' },
  { value: 'macpractice', label: 'macpractice' },
  { value: 'magic_touch', label: 'Magic Touch' },
  { value: 'max_assist', label: 'MaxAssist' },
  { value: 'maxident', label: 'MaxiDent' },
  { value: 'navetor', label: 'NaVetor' },
  { value: 'nectarvet', label: 'NectarVet' },
  { value: 'neo', label: 'Neo' },
  { value: 'nh_evolution', label: 'New Horizon/Evolution' },
  { value: 'Open Dental', label: 'Open Dental' },
  { value: 'ora', label: 'Ora' },
  { value: 'Ortho2', label: 'Ortho2' },
  { value: 'orthotrac', label: 'OrthoTrac' },
  { value: 'Oryx', label: 'Oryx' },
  { value: 'patterson', label: 'Patterson' },
  { value: 'pbs_endo', label: 'PBS Endo' },
  { value: 'periovision', label: 'Periovision' },
  { value: 'practice_web', label: 'Practice Web' },
  { value: 'practice_works', label: 'Practice Works' },
  { value: 'provet_cloud', label: 'Provet Cloud' },
  { value: 'pulse', label: 'Pulse' },
  { value: 'revolution', label: 'Revolution' },
  { value: 'scyle', label: 'Scyle' },
  { value: 'sensei_cloud', label: 'Sensei Cloud' },
  { value: 'shepherd', label: 'Shepherd' },
  { value: 'softdent', label: 'SoftDent' },
  { value: 'tdo', label: 'TDO' },
  { value: 'ThriveCloud', label: 'ThriveCloud' },
  { value: 'tops_ortho', label: 'Tops Ortho' },
  { value: 'tracker', label: 'Tracker' },
  { value: 'vetspire', label: 'Vetspire' },
  { value: 'vetstoria', label: 'Vetstoria' },
  { value: 'vyne', label: 'Vyne' },
  { value: 'waveortho', label: 'Waveortho' },
  { value: 'winoms', label: 'WinOMS' },
  { value: 'XLDent', label: 'XLDent' },
]

// EHR systems that live in the backend enum (so staff can tag PBXes for the
// integrations team's pre-launch marketing pulls) but are not yet released as
// integrations. Hidden from the customer-facing web app until launch; staff
// (internal users) still see the full list. Confirmed with Tyler Fessler,
// 2026-06-18. Remove a value here as its integration goes live.
export const INTERNAL_ONLY_EHR_VALUES = new Set([
  'tracker',
  'cleardent',
  'maxident',
  'abeldent',
])

export const BRITISH_COLUMBIA_TIME_ZONE = 'America/Vancouver'

export const LEGACY_TIMEZONE_OPTIONS: SelectOption[] = [
  'Alaska',
  'Arizona',
  'Atlantic Time (Canada)',
  'Central Time (US & Canada)',
  'Eastern Time (US & Canada)',
  'Greenwich Mean Time (UK)',
  'Hawaii',
  'Indiana (East)',
  'Mountain Time (US & Canada)',
  'Pacific Time (US & Canada)',
  'Puerto Rico',
].map((value) => ({ value, label: value }))

export const EXTENSION_DIGITS_OPTIONS: SelectOption[] = [
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
]

export const AREA_CODES_NOT_ALLOWED = new Set([
  '911',
  '899',
  '888',
  '877',
  '866',
  '855',
  '844',
  '833',
  '822',
  '800',
  '611',
  '411',
])

export const CALL_PARK_TIMEOUT_MIN = 30
export const CALL_PARK_TIMEOUT_MAX = 1800
export const CALL_PARK_TIMEOUT_RECOMMENDED = 300
export const CALL_PARK_TIMEOUT_WARN_THRESHOLD = 600
